import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import type { ChartData, ChartOptions } from "chart.js";
import { Scatter } from "react-chartjs-2";
import { getChainageData, getSafeErrorMessage } from "../services/api";
import type { ChainageCoordinate } from "../types";
import ChartFullscreenWrapper from "./ChartFullscreenWrapper";
import Loader from "./Loader";

type Props = {
  height?: number;
  excavationIds: number[];
  profileId?: number;
  hideHeader?: boolean;
  annotation?: { annotations?: Array<Record<string, unknown>> } | null;
  rangeMin?: number | string | Date;
  rangeMax?: number | string | Date;
  xScaleUnit?: string;
  onPlotVisibilityChanged?: (label: string, hidden: boolean) => void;
};

type SeriesPoint = {
  x: Date;
  y: number;
};

function normalizeChainageData(input: ChainageCoordinate[]): ChainageCoordinate[] {
  return input.map((excavation) => ({
    ...excavation,
    chainages: (excavation.chainages ?? []).map((item) => ({
      x: new Date(item.x),
      y: item.y
    }))
  }));
}

export default function ChainageChart({
  height,
  excavationIds,
  profileId,
  hideHeader,
  annotation,
  rangeMin,
  rangeMax,
  xScaleUnit,
  onPlotVisibilityChanged
}: Props) {
  const [coordinates, setCoordinates] = useState<ChainageCoordinate[]>([]);
  const [processing, setProcessing] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    setProcessing(true);
    setErrorMessage(null);

    void getChainageData(excavationIds, profileId)
      .then((result) => {
        if (!mounted) {
          return;
        }
        setCoordinates(normalizeChainageData(result));
      })
      .catch((error) => {
        if (!mounted) {
          return;
        }

        setCoordinates([]);
        setErrorMessage(getSafeErrorMessage(error, "Nepodařilo se načíst postup ražeb."));
      })
      .finally(() => {
        if (mounted) {
          setProcessing(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [excavationIds, profileId]);

  const points = useMemo(
    () => coordinates.reduce<SeriesPoint[]>((acc, curr) => acc.concat(curr.chainages as SeriesPoint[]), []),
    [coordinates]
  );

  const chartData = useMemo(() => {
    return {
      datasets: coordinates.map((coordinate) => ({
        label: coordinate.excavationName,
        data: [...(coordinate.chainages as SeriesPoint[])].sort((a, b) => +new Date(a.x) - +new Date(b.x)),
        backgroundColor: `rgba(${coordinate.gisLayerColor}, .7)`,
        pointBackgroundColor: `rgba(${coordinate.gisLayerColor}, .7)`,
        borderColor: `rgba(${coordinate.gisLayerColor}, 1)`,
        pointHoverBackgroundColor: `rgba(${coordinate.gisLayerColor}, 1)`,
        pointBorderColor: `rgba(${coordinate.gisLayerColor}, .7)`,
        pointHoverBorderColor: `rgba(${coordinate.gisLayerColor}, 1)`,
        borderWidth: 3,
        pointRadius: 7,
        pointHoverRadius: 10,
        pointBorderWidth: 2,
        fill: false,
        tension: 0,
        showLine: true,
        stepped: true,
        parsing: false
      }))
    };
  }, [coordinates]);

  const chartOptions = useMemo(() => {
    const unit = xScaleUnit ?? (coordinates.length > 0 ? coordinates[0].unit ?? "day" : "day");

    const xValues = points.map((item) => +new Date(item.x));
    const yValues = points.map((item) => item.y);

    const xAxisMin = rangeMin ? moment(rangeMin).startOf("day").valueOf() : (xValues.length > 0 ? moment(Math.min(...xValues)).startOf("day").valueOf() : undefined);
    const xAxisMax = rangeMax ? moment(rangeMax).startOf("day").valueOf() : (xValues.length > 0 ? moment(Math.max(...xValues)).startOf("day").valueOf() : undefined);

    const yAxisMin = profileId
      ? -20
      : yValues.length > 0
        ? Math.min(...yValues)
        : -1;
    const yAxisMax = profileId
      ? 20
      : yValues.length > 0
        ? Math.max(...yValues)
        : 1;

    const annotationEntries = annotation?.annotations ?? [];
    const annotationMap: Record<string, unknown> = {};
    annotationEntries.forEach((item, index) => {
      annotationMap[`ann-${index}`] = {
        ...item,
        label: {
          ...(item.label ?? {}),
          display: true,
          content: (item.label as { content?: string } | undefined)?.content
        }
      };
    });

    return {
      responsive: true,
      plugins: {
        legend: {
          display: hideHeader !== true && coordinates.length > 1,
          onClick: (
            _event: unknown,
            legendItem: { datasetIndex?: number },
            legend: {
              chart: {
                data?: { datasets?: Array<{ label?: string }> };
                isDatasetVisible: (index: number) => boolean;
                setDatasetVisibility: (index: number, visible: boolean) => void;
                update: () => void;
              };
            }
          ) => {
            const chart = legend?.chart;
            const datasetIndex = legendItem?.datasetIndex;
            if (!chart || datasetIndex == null) {
              return;
            }

            const nextVisibility = !chart.isDatasetVisible(datasetIndex);
            chart.setDatasetVisibility(datasetIndex, nextVisibility);
            chart.update();

            const label = chart.data?.datasets?.[datasetIndex]?.label;
            if (typeof label === "string") {
              onPlotVisibilityChanged?.(label, !nextVisibility);
            }
          }
        },
        title: {
          display: hideHeader !== true,
          text: "Excavations"
        },
        annotation:
          Object.keys(annotationMap).length > 0
            ? {
                annotations: annotationMap
              }
            : undefined
      },
      scales: {
        "x-axis-1": {
          type: "time",
          position: "bottom",
          min: xAxisMin,
          max: xAxisMax,
          time: {
            unit: unit === "automatic" ? undefined : unit,
            tooltipFormat: "DD.MM.YYYY",
            displayFormats: {
              day: "DD.MM.YY",
              week: "DD.MM.YY",
              month: "MM/YY",
              hour: "DD.MM.YY HH:mm"
            }
          },
          grid: {
            color: "rgba(137, 139, 186, .3)",
            display: true
          },
          title: {
            display: true,
            text: "Measurement Date"
          }
        },
        "y-axis-1": {
          type: "linear",
          min: yAxisMin,
          max: yAxisMax,
          grid: {
            color: "rgba(137, 139, 186, .3)",
            display: true
          },
          title: {
            display: true,
            text: "Excavations Progress [tm]"
          }
        }
      }
    };
  }, [annotation?.annotations, coordinates, hideHeader, points, profileId, rangeMax, rangeMin, xScaleUnit]);

  if (processing) {
    return <Loader />;
  }

  if (errorMessage) {
    return (
      <div className="alert alert-warning" role="alert">
        {errorMessage}
      </div>
    );
  }

  return (
    <ChartFullscreenWrapper>
      <Scatter
        data={chartData as unknown as ChartData<"scatter">}
        options={chartOptions as unknown as ChartOptions<"scatter">}
        height={height}
      />
    </ChartFullscreenWrapper>
  );
}
